<script lang="ts">
  import { ShoppingCart, X } from '@lucide/svelte'
  import { getContext } from 'svelte'
  import { syncSiteCart } from '~/lib/site-cart'
  import { SITE_CART_KEY, type SiteCartStore } from '~/lib/site-cart-store.svelte'
  import { siteCartStore } from '~/lib/site-cart-store.svelte'
  import { resolveSiteCart } from '~/lib/site-cart-view'
  import { page } from '@inertiajs/svelte'
  import { Dialog } from 'bits-ui'
  import ProductPlaceholder from '~/components/site/ProductPlaceholder.svelte'
  import { SITE_UI_KEY, type SiteUiState } from '~/lib/site-ui-context.svelte'
  import type { SiteCartSnapshot } from '~/lib/site-catalog'
  import { siteBtnOutlineClass, siteBtnPrimaryClass, siteDialogOverlayClass } from '~/app/site-theme'
  import { cn } from '~/lib/utils'

  const siteUi = getContext<SiteUiState>(SITE_UI_KEY)
  const cartStore = getContext<SiteCartStore>(SITE_CART_KEY)

  const siteCart = $derived(
    resolveSiteCart(siteCartStore.snapshot, $page.props as Record<string, unknown>)
  )

  function updateQty(item: SiteCartSnapshot['items'][number], quantity: number) {
    const product = {
      id: item.product.id,
      name: item.product.name,
      slug: item.product.slug,
      description: null,
      price: item.product.price,
      quantity: item.product.stockQuantity,
      imageUrls: item.product.imageUrl ? [item.product.imageUrl] : [],
      imageUrl: item.product.imageUrl,
      inStock: item.product.stockQuantity > 0,
    }
    void syncSiteCart(cartStore, product, quantity)
  }

  function lineMax(item: SiteCartSnapshot['items'][number]): number {
    return item.product.stockQuantity
  }
</script>

<Dialog.Root bind:open={siteUi.cartDrawerOpen}>
  <Dialog.Portal>
    <Dialog.Overlay class={siteDialogOverlayClass} />
    <Dialog.Content
      class={cn(
        'fixed inset-y-0 right-0 z-[61] flex w-full max-w-md flex-col border-l border-[rgba(122,181,71,0.25)] bg-[#111d12] shadow-2xl outline-none'
      )}
    >
      <div class="flex items-center justify-between border-b border-[rgba(122,181,71,0.15)] px-5 py-4">
        <div class="flex items-center gap-2">
          <ShoppingCart class="size-5 text-[#7AB547]" />
          <Dialog.Title class="text-lg font-bold text-white">Корзина</Dialog.Title>
        </div>
        <Dialog.Close
          class="rounded-lg p-2 text-[#dfe8d8] hover:bg-[#7AB547]/10 hover:text-white"
          aria-label="Закрыть"
        >
          <X class="size-5" />
        </Dialog.Close>
      </div>

      <div class="flex-1 overflow-y-auto px-5 py-4">
        {#if siteCart.items.length === 0}
          <p class="py-12 text-center text-sm text-[#dfe8d8]">Корзина пуста</p>
        {:else}
          <ul class="space-y-3">
            {#each siteCart.items as item (item.productId)}
              <li
                class="flex gap-3 rounded-xl border border-[rgba(122,181,71,0.15)] bg-[#09100a]/50 p-3"
              >
                <div class="size-16 shrink-0 overflow-hidden rounded-lg bg-[#182619]">
                  {#if item.product.imageUrl}
                    <img src={item.product.imageUrl} alt="" class="size-full object-cover" />
                  {:else}
                    <ProductPlaceholder name={item.product.name} class="size-full text-[10px]" />
                  {/if}
                </div>
                <div class="min-w-0 flex-1">
                  <p class="truncate font-medium text-white">{item.product.name}</p>
                  <p class="text-xs text-[#dfe8d8]">{item.product.price} ₽ / кг</p>
                  <div
                    class="mt-2 inline-flex overflow-hidden rounded-lg border border-[rgba(122,181,71,0.25)]"
                  >
                    <button
                      type="button"
                      class="px-2 py-1 text-sm text-[#7AB547] hover:bg-[#7AB547]/10"
                      onclick={() => updateQty(item, item.quantity - 1)}
                      aria-label="Уменьшить"
                    >
                      −
                    </button>
                    <span class="min-w-8 px-1 py-1 text-center text-sm text-white"
                      >{item.quantity}</span
                    >
                    <button
                      type="button"
                      class="px-2 py-1 text-sm text-[#7AB547] hover:bg-[#7AB547]/10 disabled:opacity-30"
                      disabled={item.quantity >= lineMax(item)}
                      onclick={() =>
                        updateQty(item, Math.min(lineMax(item), item.quantity + 1))}
                      aria-label="Увеличить"
                    >
                      +
                    </button>
                  </div>
                  <p class="mt-1 text-sm font-semibold text-[#7AB547]">{item.lineTotal} ₽</p>
                </div>
              </li>
            {/each}
          </ul>
        {/if}
      </div>

      <div class="border-t border-[rgba(122,181,71,0.15)] p-5">
        <div class="mb-4 flex items-baseline justify-between">
          <span class="text-[#dfe8d8]">Итого</span>
          <span class="text-2xl font-bold text-[#7AB547]">{siteCart.total} ₽</span>
        </div>
        <a
          href="/checkout"
          class="{siteBtnPrimaryClass} mb-2 block w-full text-center"
          onclick={() => siteUi.closeCartDrawer()}
        >
          Оформить заказ
        </a>
        <a
          href="/cart"
          class="{siteBtnOutlineClass} block w-full text-center text-sm"
          onclick={() => siteUi.closeCartDrawer()}
        >
          Открыть корзину полностью
        </a>
      </div>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
